import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "@/components/ui";
import { useFormatters } from "@/lib/i18n/format";
import { useNotifications, type Notification } from "@/lib/notifications/notifications";

/**
 * Humanize a raw notification `type` (e.g. "messageReceived" → "Message received") as a defensive
 * fallback when a type has no entry under `notifications.types.*`. Every shipped type is in the
 * catalogue; this only guards a future type added server-side before its label lands.
 */
function humanizeType(type: string): string {
  const words = type
    .replace(/[._]/g, " ")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The shell's bell: an unread badge over the notifications icon plus a popover panel listing
 * recent notifications, with mark-as-read (one + all). Shared by both shells (architecture
 * §6.2). Styling is tokens-only (`.notif-*` in globals.css); the badge reuses the existing
 * `.icon-badge`. Self-contained popover (outside-click + Escape) so inner clicks don't close it.
 */
export function NotificationsBell() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { items, unreadCount, refresh, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    refresh();
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, refresh]);

  function title(n: Notification): string {
    // The notification `type` IS the catalogue key under `notifications.types.*`; humanize as a
    // defensive fallback for any type not yet translated.
    const label = t(`notifications.types.${n.type}`, { defaultValue: humanizeType(n.type) });
    const reference = n.payload?.reference;
    return typeof reference === "string" ? `${label} · ${reference}` : label;
  }

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <IconButton
        name="bell"
        label={t("shell.notifications")}
        active={open}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        badge={unreadCount > 0 ? (unreadCount > 9 ? "9+" : unreadCount) : undefined}
      />
      {open && (
        <div className="dropdown notif-panel" role="menu">
          <div className="notif-head">
            <b>{t("notifications.title")}</b>
            {unreadCount > 0 && (
              <button type="button" className="notif-link" onClick={() => void markAllRead()}>
                {t("notifications.markAllRead")}
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="notif-empty muted">{t("notifications.empty")}</p>
          ) : (
            <ul className="notif-list">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    className="notif-item"
                    onClick={() => void markRead(n.id)}
                    aria-label={title(n)}
                  >
                    {!n.readAt && <span className="notif-dot" aria-hidden />}
                    <span className="notif-body">
                      <span className="notif-title">{title(n)}</span>
                      <span className="notif-time">{fmt.dateTime(n.createdAt)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
